import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ChipInput } from "@/components/app/ChipInput";
import { InfoTip } from "@/components/app/Common";
import { api, errMsg } from "@/lib/api";
import { LOW_CONF_LABELS } from "@/lib/candidates";
import { ExperienceEditor, EducationEditor } from "@/pages/candidates/SectionEditors";
import { cn } from "@/lib/utils";

const Section = ({ id, title, low, children }) => (
  <section className={cn("panel space-y-3 p-5", low && "border-[#E8D7B0] bg-[#FDFBF7]")} data-testid={`parsed-section-${id}`}>
    <div className="flex items-center justify-between">
      <h3 className="text-base font-semibold">{title}</h3>
      {low && <span className="inline-flex items-center gap-1 rounded-full bg-caution-tint px-2 py-0.5 text-xs font-medium text-caution" data-testid={`low-confidence-${id}`}><AlertTriangle size={12} /> Check this</span>}
    </div>
    {children}
  </section>
);

export function ParsedEditor({ candidate, canEdit }) {
  const qc = useQueryClient();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setF({ name: candidate.name, email: candidate.email || "", phone: candidate.phone || "", location: candidate.location, linkedin: candidate.linkedin, parsed: candidate.parsed });
  }, [candidate]);
  if (!f) return null;
  const low = new Set(candidate.low_confidence || []);
  const setP = (k, v) => setF((x) => ({ ...x, parsed: { ...x.parsed, [k]: v } }));
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      const body = { name: f.name, location: f.location, linkedin: f.linkedin, parsed: f.parsed };
      if (canEdit && f.email && !f.email.includes("•")) body.email = f.email;
      if (canEdit && f.phone && !f.phone.includes("•")) body.phone = f.phone;
      await api.patch(`/candidates/${candidate.id}`, body);
      await qc.invalidateQueries({ queryKey: ["candidate", candidate.id] });
      toast.success("Candidate details saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <fieldset disabled={!canEdit} className="space-y-4" data-testid="parsed-editor">
      {low.size > 0 && (
        <p className="rounded-lg bg-caution-tint px-4 py-3 text-sm text-caution" data-testid="low-confidence-banner">
          We weren't sure about: {[...low].map((k) => LOW_CONF_LABELS[k] || k).join(", ")}. Check the highlighted sections against the original.
        </p>
      )}
      <Section id="contact" title="Contact" low={low.has("contact")}>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1"><Label className="text-xs">Name</Label><Input value={f.name} onChange={set("name")} data-testid="parsed-name-input" /></div>
          <div className="space-y-1"><Label className="text-xs">Email</Label><Input value={f.email} onChange={set("email")} data-testid="parsed-email-input" /></div>
          <div className="space-y-1"><Label className="text-xs">Phone</Label><Input value={f.phone} onChange={set("phone")} data-testid="parsed-phone-input" /></div>
          <div className="space-y-1"><Label className="text-xs">Location</Label><Input value={f.location} onChange={set("location")} data-testid="parsed-location-input" /></div>
          <div className="space-y-1 sm:col-span-2"><Label className="text-xs">LinkedIn</Label><Input value={f.linkedin} onChange={set("linkedin")} data-testid="parsed-linkedin-input" /></div>
        </div>
      </Section>
      <Section id="summary" title="Summary" low={low.has("summary")}>
        <Textarea value={f.parsed.summary} onChange={(e) => setP("summary", e.target.value)} className="min-h-[72px] text-sm" data-testid="parsed-summary-input" />
        <div className="flex items-center gap-2 text-sm">
          <Label className="text-xs">Total experience (years)</Label>
          <InfoTip text="Calculated from the role dates, with overlapping roles counted once." />
          <Input type="number" step="0.1" value={f.parsed.total_years_experience ?? ""} className="h-8 w-24" data-testid="parsed-total-years-input"
            onChange={(e) => setP("total_years_experience", e.target.value === "" ? null : Number(e.target.value))} />
        </div>
      </Section>
      <Section id="skills" title="Skills" low={low.has("skills")}>
        <ChipInput values={f.parsed.skills} onChange={(v) => setP("skills", v)} testId="parsed-skills" />
      </Section>
      <Section id="experience" title="Experience" low={low.has("experience")}>
        <ExperienceEditor items={f.parsed.experience} onChange={(v) => setP("experience", v)} />
      </Section>
      <Section id="education" title="Education" low={low.has("education")}>
        <EducationEditor items={f.parsed.education} onChange={(v) => setP("education", v)} />
      </Section>
      <Section id="certifications" title="Certifications and languages" low={low.has("certifications")}>
        <ChipInput values={f.parsed.certifications} onChange={(v) => setP("certifications", v)} testId="parsed-certifications" tone="teal" />
        <ChipInput values={f.parsed.languages} onChange={(v) => setP("languages", v)} testId="parsed-languages" tone="teal" placeholder="Add a language" />
      </Section>
      {canEdit && (
        <div className="sticky bottom-4 flex justify-end">
          <Button onClick={save} disabled={busy} className="shadow-lg" data-testid="parsed-save-btn">{busy && <Loader2 size={16} className="animate-spin" />} Save details</Button>
        </div>
      )}
    </fieldset>
  );
}
